import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const GrossProfitMargin = () => {
  // State variables for Gross Profit Margin calculation
  const [revenue, setRevenue] = useState(0); // Revenue from sales
  const [costOfGoodsSold, setCostOfGoodsSold] = useState(0); // Cost of goods sold (COGS)
  const [grossProfitMargin, setGrossProfitMargin] = useState(0); // Calculated gross profit margin

  // Event handlers to update state values
  const handleRevenueChange = (e) => setRevenue(parseFloat(e.target.value) || 0);
  const handleCostOfGoodsSoldChange = (e) => setCostOfGoodsSold(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate gross profit margin when any input changes
  useEffect(() => {
    if (revenue && costOfGoodsSold >= 0) {
      const margin = ((revenue - costOfGoodsSold) / revenue) * 100;
      setGrossProfitMargin(margin);
    } else {
      setGrossProfitMargin(0);
    }

     // This will initialize or reinitialize all popovers on the page whenever the component renders
     const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [revenue, costOfGoodsSold]);

  return (
    <Layout title="Gross Profit Margin Calculator" path="gross-profit-margin">
      <section className="section-padding" id="gross-profit-margin-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Gross Profit Margin Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Gross Profit Margin Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Revenue Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="revenue" className="form-label">
                        Revenue (₹)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Revenue:</strong> The total income generated from sales or services, before deducting any costs."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="revenue"
                        placeholder="Enter total revenue"
                        value={revenue || ""}
                        onChange={handleRevenueChange}
                      />
                    </div>

                    {/* Cost of Goods Sold Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="costOfGoodsSold" className="form-label">
                        Cost of Goods Sold (₹)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Cost of Goods Sold (COGS):</strong> The direct costs attributable to the production of the goods sold in a company."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="costOfGoodsSold"
                        placeholder="Enter cost of goods sold"
                        value={costOfGoodsSold || ""}
                        onChange={handleCostOfGoodsSoldChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Gross Profit Margin:{" "}
                        <span className="fw-bold ms-3">
                          {grossProfitMargin === 0 ? "0.00" : grossProfitMargin.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Gross Profit Margin Calculator
                </h3>

                {/* What is a Gross Profit Margin Calculator? */}
                <h4 className="mt-3 mb-3">What is a Gross Profit Margin Calculator?</h4>
                <p>
                  A **Gross Profit Margin Calculator** helps you determine the percentage of revenue that exceeds the cost of goods sold (COGS). It provides insight into the profitability of your business operations and helps track financial performance.
                </p>

                {/* Why Use a Gross Profit Margin Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a Gross Profit Margin Calculator?</h4>
                <p>
                  This calculator is essential for businesses to evaluate how efficiently they are producing and selling goods or services. A higher gross profit margin indicates better profitability, while a lower margin suggests the need for cost improvements.
                </p>

                {/* Benefits of Using a Gross Profit Margin Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using a Gross Profit Margin Calculator</h4>
                <ul>
                  <li><strong>Helps in Assessing Profitability of Products or Services:</strong> A higher margin means more revenue is retained after covering the cost of production.</li>
                  <li><strong>Guides Pricing Strategies:</strong> Understanding the gross profit margin helps set competitive prices while maintaining profitability.</li>
                  <li><strong>Useful for Tracking Business Growth:</strong> The calculator helps identify trends and patterns in your business performance over time.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use a Gross Profit Margin Calculator?</h4>
                <p>
                  This calculator is suitable for:
                </p>
                <ul>
                  <li><strong>Retailers:</strong> Helps retailers monitor profitability per product.</li>
                  <li><strong>Manufacturers:</strong> Aids manufacturers in tracking production costs and pricing strategies.</li>
                  <li><strong>E-commerce Businesses:</strong> Useful for online businesses to assess the profitability of different product categories.</li>
                  <li><strong>Service Industries:</strong> Helps service providers track their operational costs versus their service fees.</li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">What is a good gross profit margin?</h4>
                <p>
                  A good gross profit margin varies by industry. However, a higher percentage is generally better, indicating that a company is efficiently managing its production costs. On average, a gross profit margin of 50% or more is considered good in many industries.
                </p>

                <h4 className="mt-4 mb-3">What can I do to improve my gross profit margin?</h4>
                <p>
                  You can improve your gross profit margin by either increasing your revenue (e.g., by raising prices or boosting sales) or reducing your cost of goods sold (e.g., by negotiating better supplier deals or improving operational efficiency).
                </p>

                <h4 className="mt-4 mb-3">How does the gross profit margin differ from net profit margin?</h4>
                <p>
                  Gross profit margin only considers revenue and the cost of goods sold, while net profit margin includes all expenses, such as operating costs, taxes, and interest. The net profit margin gives a more comprehensive view of a company's profitability.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default GrossProfitMargin;
